// ============================================================================
// Authenticated WebformHub resource bridge
// ============================================================================
//
// /dailys/* is login-required, while WebformHub intentionally remains a plain
// presenter in src/webforms. This app-side seam owns the authenticated Query
// scope and injects the former mount lookup values as plain props. Do not move
// this into src/webforms: a presenter must not choose an authenticated scope
// for farm data.
//
// IT ALSO OWNS THE TWO LAYER DIRECTORIES NOW, AND THAT IS A DIFFERENT KIND OF
// TRANSFER FROM THE TWO ABOVE IT.
//
// `webform.hub.inputs` and `webform.hub.config` replaced reads the PRESENTER
// used to perform on mount. `webform.hub.layerDirectory` replaces something the
// presenter never read: App loaded layer_batches and layer_housings once at
// boot and threaded both arrays down through WebformsEntry as props, so the hub
// consumed a former-eager global result without ever naming a table. Owning
// that read here is what lets view.webformhub retire from the frozen
// layer_batches / layer_housings consumer lists in
// tests/fixtures/former-eager-dependencies.json.
//
// The corollary matters more than the change: those two values must never
// travel as WebformsEntry props again. The provenance derivation scores a prop
// hand-off in a domain entry as consumption, so reinstating either one there
// makes the retirement false and fails the guard by name.
//
// AND IT NOW OWNS THE TWO app_store DOCUMENTS, WHICH IS WHAT EMPTIED THIS
// SURFACE'S RESIDUAL LIST.
//
// `layerGroups` (ppp-layer-groups-v1) and `webformsConfig` (ppp-webforms-v1)
// were the last two values the hub took off App's global boot, through
// WebformsEntry props filled from LayerContext and WebformsConfigContext. They
// are read here now, in this surface's own authenticated scope, through
// layer.eggDailys.groups and webform.pigDailys.config — the resources that
// already own those exact two keys for the egg index and the direct Pig Dailys
// form. That is what lets view.webformhub retire from the frozen app_store
// consumer list in tests/fixtures/former-eager-dependencies.json, and what let
// `residualLegacy` empty.
//
// THE ONE THING THAT CHANGED BESIDES THE OWNER IS WHEN THE VALUE ARRIVES.
//
// App's `if (!dataLoaded)` gate used to guarantee both documents had already
// landed before this presenter first rendered. A scoped query has not resolved
// on the first frame, so a value read ONCE at mount would be lost — and the egg
// form's four default group names are read exactly once, by useState. The
// presenter applies those four when they arrive instead; see its egg-defaults
// effect, and the differential oracle in
// tests/static/webform_hub_egg_group_defaults_static.test.js that pins the four
// values across the move.
// ============================================================================
import React from 'react';

import {useAuth} from '../../contexts/AuthContext.jsx';
import {DEFAULT_WEBFORMS_CONFIG} from '../../lib/defaults.js';
import WebformHub from '../../webforms/WebformHub.tsx';
import {RESOURCE} from './routeDataCatalog.js';
import type {
  EggDailysGroupsData,
  WebformHubConfigData,
  WebformHubInputsData,
  WebformHubLayerDirectoryData,
  WebformPigDailysConfigData,
} from './routeDataCatalog.js';
import {useRouteResource} from './useRouteResource.js';
import {resolveLayerGroupStatus} from '../../lib/layerGroupStatus.js';
import {useEggDailysProjectionRefresh} from '../../layer/eggDailysProjectionRefresh.js';
import {useLayerDailysProjectionRefresh} from '../../layer/layerDailysProjectionRefresh.js';
import type {WebformHubLookupConfig, WebformHubRecord, WebformHubResourceProps} from '../../webforms/WebformHub.js';
import type {SessionSubmitter} from '../../webforms/webformsRoutes.js';
import type {DailyReportAuthState} from '../../lib/dailyReportsApi.js';
import {visibleDailyHistoryPrograms} from '../../lib/access.js';
import type {AuthSession} from '../../lib/access.js';

type WebformHubAuthenticatedBridgeProps = {
  children: React.ReactElement<WebformHubResourceProps>;
};

type AuthenticatedWebformHubResourcesProps = WebformHubAuthenticatedBridgeProps & {
  reportEditor: DailyReportAuthState;
};

const WEBFORM_HUB_RESOURCE_PARAMS = Object.freeze({});

const EMPTY_WEBFORM_HUB_LOOKUP_CONFIG: WebformHubLookupConfig = Object.freeze({
  fullConfig: null,
  broilerGroups: [],
  broilerBatchMeta: [],
  webformSettings: {},
  activePigGroups: [],
  housingBatchMap: {},
});

/**
 * Convert raw catalog payloads into exactly the values the old mount effect
 * would have placed in presenter state. Keep this here, beside the query
 * owner, so WebformHub remains data-source agnostic.
 */
export function projectWebformHubLookupConfig(data: WebformHubConfigData | null | undefined): WebformHubLookupConfig {
  if (!data) return EMPTY_WEBFORM_HUB_LOOKUP_CONFIG;

  const rawBroilerGroups = data.broilerGroups;
  const rawBroilerMeta = data.broilerBatchMeta;
  const rawActiveGroups = data.activeGroups;

  return {
    fullConfig: data.fullConfig ? (data.fullConfig as WebformHubRecord) : null,
    broilerGroups:
      Array.isArray(rawBroilerGroups) && rawBroilerGroups.length > 0
        ? (rawBroilerGroups as Array<string | null | undefined>)
        : [],
    broilerBatchMeta: Array.isArray(rawBroilerMeta)
      ? (rawBroilerMeta as WebformHubLookupConfig['broilerBatchMeta'])
      : [],
    webformSettings: data.webformSettings ? (data.webformSettings as WebformHubRecord) : {},
    activePigGroups:
      Array.isArray(rawActiveGroups) && rawActiveGroups.length > 0
        ? rawActiveGroups.map((name) => ({value: name, label: name}) as WebformHubRecord)
        : [],
    housingBatchMap: data.housingBatchMap ? (data.housingBatchMap as Record<string, any>) : {},
  };
}

function AuthenticatedWebformHubResources({children, reportEditor}: AuthenticatedWebformHubResourcesProps) {
  // The presenter submits Egg and Layer records but must not choose an
  // authenticated Layer/context scope for either shared action-time projection.
  // Keep both hooks in this authenticated resource seam and inject their
  // callbacks instead.
  const refreshEggDailysProjection = useEggDailysProjectionRefresh();
  const refreshLayerDailysProjection = useLayerDailysProjectionRefresh();
  // These former mount effects were independent. Pending and rejected lookup
  // reads must leave the selector/form controls at their existing defaults,
  // rather than replacing the daily form with a loading or error screen.
  const {data: inputsData} = useRouteResource(RESOURCE.WEBFORM_HUB_INPUTS, WEBFORM_HUB_RESOURCE_PARAMS);
  const {data: configData} = useRouteResource(RESOURCE.WEBFORM_HUB_CONFIG, WEBFORM_HUB_RESOURCE_PARAMS);
  // The two layer directories used to arrive as App boot arrays threaded
  // through WebformsEntry. They are read here now, in the same authenticated
  // scope as the two lookups above, so the presenter's dropdown and its
  // label→batch_id resolver stop consuming the global boot result.
  const {data: layerDirectoryData} = useRouteResource(
    RESOURCE.WEBFORM_HUB_LAYER_DIRECTORY,
    WEBFORM_HUB_RESOURCE_PARAMS,
  );
  // The two app_store documents, each from the resource that already owns its
  // key. Two reads rather than one combined one because neither key is new
  // here: joining them would mean a third query function and a third
  // inventoried physical call site for values two existing resources already
  // read correctly.
  const {
    data: layerGroupsData,
    error: layerGroupsError,
    refetch: retryLayerGroups,
  } = useRouteResource(RESOURCE.EGG_DAILYS_GROUPS, WEBFORM_HUB_RESOURCE_PARAMS);
  const {data: appStoreConfigData} = useRouteResource(RESOURCE.WEBFORM_PIG_DAILYS_CONFIG, WEBFORM_HUB_RESOURCE_PARAMS);
  const webformHubLookupConfig = React.useMemo(
    () => projectWebformHubLookupConfig(configData as WebformHubConfigData | null),
    [configData],
  );
  const webformHubFeedInputs = ((inputsData as WebformHubInputsData | null)?.feedInputs || []) as WebformHubRecord[];
  const layerDirectory = layerDirectoryData as WebformHubLayerDirectoryData | null;
  const layerBatches = (layerDirectory?.layerBatches || []) as WebformHubRecord[];
  const layerHousings = (layerDirectory?.layerHousings || []) as WebformHubRecord[];
  // Pending resolves to [], which is exactly what LayerContext held before
  // App's boot published the blob, and exactly what the resource resolves a
  // missing row or a failed read to. The presenter filters this to the active
  // rows and takes the first four as the egg form's default group names.
  const layerGroups = ((layerGroupsData as EggDailysGroupsData | null)?.layerGroups || []) as WebformHubRecord[];
  // THE ROWS ARE NOT ENOUGH, AND THE `|| []` ABOVE IS EXACTLY WHY.
  //
  // `[]` is what a pending read, a failed read and a farm with no layer groups
  // all look like. The presenter's layer dropdown is otherwise derived from
  // layer_batches / layer_housings / the webform_config mirror, every one of
  // which lags a status change by up to a maintenance run — so reading `[]` as
  // "resolved, nothing retired" would offer a switched-off group at precisely
  // the moment the canonical answer is unavailable. The verdict is threaded
  // beside the rows so the presenter can tell the three apart.
  const layerGroupStatus = resolveLayerGroupStatus({
    data: layerGroupsData as EggDailysGroupsData | null,
    error: layerGroupsError,
  });
  // Same posture as PigDailysAuthenticatedBridge, for the same document and the
  // same reason: DEFAULT_WEBFORMS_CONFIG is literally what WebformsConfigContext
  // holds until App's boot replaces it, so a pending read renders the forms
  // exactly as an unhydrated context did, and the resource resolves a failed or
  // absent row to the same constant. It is the SECOND-choice document either
  // way — webform.hub.config's webform_config mirror wins in the presenter
  // whenever it has resolved, and this is the app_store original that mirror is
  // republished from.
  const webformsConfig = ((appStoreConfigData as WebformPigDailysConfigData | null)?.webformsConfig ??
    DEFAULT_WEBFORMS_CONFIG) as WebformHubRecord;

  return React.cloneElement(children, {
    webformHubLookupConfig,
    webformHubFeedInputs,
    layerBatches,
    layerHousings,
    layerGroups,
    layerGroupStatus,
    onRetryLayerGroupStatus: retryLayerGroups,
    webformsConfig,
    refreshEggDailysProjection,
    refreshLayerDailysProjection,
    reportEditor,
  });
}

export default function WebformHubAuthenticatedBridge({children}: WebformHubAuthenticatedBridgeProps) {
  const {authState} = useAuth();

  // App's login gate makes this normally unreachable, but fail closed locally:
  // no unresolved/logged-out render may build a public-scope farm query.
  if (authState === null || authState === false) return null;

  // Only the role and id: what the hub needs to tell a submitter whether a
  // report blocking theirs is their own and still editable by them.
  const reportEditor: DailyReportAuthState = {role: authState.role, user: {id: authState.user?.id}};

  return <AuthenticatedWebformHubResources reportEditor={reportEditor}>{children}</AuthenticatedWebformHubResources>;
}

// ============================================================================
// The mount for the RELEASED `/dailys` hub surface
// ============================================================================
//
// `renderReleasedAuthenticatedSurface` is a plain function, so it cannot call
// `useAuth` itself. App normally gives WebformsEntry all three of the props
// below, but App does not render on a released route. They remain
// reconstructible from AuthContext alone: WebformHub reads only the submitter
// name, and its two visibility flags are the exact role predicates App uses.
// Keep that reconstruction on this authenticated seam rather than importing
// AuthContext into src/webforms; the presenter is intentionally auth-free.
//
// This is additive. WebformsEntry keeps its legacy App-prop path intact for
// every refused `/dailys/*` pathname, while this mount owns only a released
// canonical whitelist claim.
export function WebformHubAuthenticatedMount() {
  const {authState} = useAuth();

  // Same local fail-closed guard as the resource bridge: an unresolved or
  // logged-out session must never create a farm-scoped resource owner.
  if (authState === null || authState === false) return null;

  // WebformHub consumes only `name`; this is App's exact fallback for that
  // member, without inventing the unused email/profileId/role fields.
  const sessionSubmitter: SessionSubmitter = {
    name: authState.name || authState.user?.email || '',
  };

  return (
    <WebformHubAuthenticatedBridge>
      <WebformHub
        sessionSubmitter={sessionSubmitter}
        hideWeighIns={authState.role === 'light'}
        // The server rejects equipment technicians at Cattle Log, so preserve
        // the legacy hub's matching affordance guard.
        hideCattleLog={authState.role === 'equipment_tech'}
        dailyHistoryPrograms={visibleDailyHistoryPrograms(authState as AuthSession)}
      />
    </WebformHubAuthenticatedBridge>
  );
}
